import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContextType';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

interface DistStats { count: number; mean?: number; min?: number; max?: number; p95?: number; p99?: number; }

interface MetricsPayload {
  counters: Record<string, number>;
  gauges: Record<string, number>;
  timers: Record<string, DistStats>;
  histograms: Record<string, DistStats>;
  metadata: { last_reset: string; uptime_seconds: number };
}

const fmt = (n: number, decimals = 3) => Number(n).toFixed(decimals);
const fmtUptime = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const MetricTable = ({ data, cols }: { data: [string, number | DistStats][]; cols: { key: string; label: string }[] }) => (
  <table className="w-full text-xs">
    <thead>
      <tr className="border-b border-slate-100">
        <th className="text-left py-1 pr-4 font-medium text-slate-500 font-mono">Metric</th>
        {cols.map(c => <th key={c.key} className="text-right py-1 px-2 font-medium text-slate-500 w-20">{c.label}</th>)}
      </tr>
    </thead>
    <tbody>
      {data.map(([name, val]) => (
        <tr key={name} className="border-b border-slate-50 hover:bg-slate-50">
          <td className="py-1 pr-4 font-mono text-slate-700 break-all">{name}</td>
          {cols.map(c => (
            <td key={c.key} className="text-right py-1 px-2 tabular-nums text-slate-600">
              {typeof val === 'number'
                ? String(Number(val))
                : val[c.key as keyof DistStats] !== undefined
                  ? fmt(val[c.key as keyof DistStats] as number)
                  : '—'}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export default function SettingsMetrics() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSysAdmin = user && ['SysAdmin', 'DevOps'].includes(user.role ?? '');

  const [data, setData] = useState<MetricsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setResetMsg(null);
    try {
      const { data: d } = await api.get('/metrics');
      setData(d);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e?.response?.data?.detail ?? t('pages.settingsMetrics.errorLoad'));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleReset = async () => {
    if (!window.confirm(t('pages.settingsMetrics.resetConfirm'))) return;
    setResetting(true); setResetMsg(null); setError(null);
    try {
      await api.post('/metrics/reset');
      setResetMsg(t('pages.settingsMetrics.resetSuccess'));
      await load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e?.response?.data?.detail ?? t('pages.settingsMetrics.errorReset'));
    } finally { setResetting(false); }
  };

  if (!isSysAdmin) return null;

  const counters = data ? Object.entries(data.counters) : [];
  const gauges = data ? Object.entries(data.gauges) : [];
  const timers = data ? Object.entries(data.timers) : [];
  const histograms = data ? Object.entries(data.histograms) : [];
  const hasAny = counters.length + gauges.length + timers.length + histograms.length > 0;

  const distCols = [
    { key: 'count', label: t('pages.settingsMetrics.colCount') },
    { key: 'mean',  label: t('pages.settingsMetrics.colMean') },
    { key: 'p95',   label: t('pages.settingsMetrics.colP95') },
    { key: 'p99',   label: t('pages.settingsMetrics.colP99') },
    { key: 'min',   label: t('pages.settingsMetrics.colMin') },
    { key: 'max',   label: t('pages.settingsMetrics.colMax') },
  ];

  return (
    <>
      <PageHeader title={t('pages.settingsMetrics.title')} subtitle={t('pages.settingsMetrics.subtitle')} />

      <div className="px-6 pb-6 space-y-5 pt-4">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          {data && (
            <div className="flex gap-4 text-xs text-slate-500 flex-1">
              <span>{t('pages.settingsMetrics.uptime')}: <span className="font-semibold text-slate-700">{fmtUptime(data.metadata.uptime_seconds)}</span></span>
              <span>{t('pages.settingsMetrics.lastReset')}: <span className="font-semibold text-slate-700">{new Date(data.metadata.last_reset).toLocaleString()}</span></span>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>{loading ? t('pages.settingsMetrics.loading') : t('pages.settingsMetrics.btnRefresh')}</Button>
          <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={handleReset} disabled={resetting || loading}>
            {t('pages.settingsMetrics.btnReset')}
          </Button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {resetMsg && <p className="text-sm text-green-700">{resetMsg}</p>}

        {!hasAny && data && (
          <div className="flex flex-col items-center py-12 gap-2 text-slate-400">
            <Activity size={28} strokeWidth={1.5} />
            <p className="text-sm font-medium text-slate-500">{t('pages.settingsMetrics.noData')}</p>
            <p className="text-xs">{t('pages.settingsMetrics.noDataHint')}</p>
          </div>
        )}

        {counters.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.settingsMetrics.sectionCounters')}</h2>
            <MetricTable data={counters as [string, number][]} cols={[{ key: 'value', label: t('pages.settingsMetrics.colValue') }]} />
          </section>
        )}

        {gauges.length > 0 && (
          <>
            {counters.length > 0 && <Separator />}
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.settingsMetrics.sectionGauges')}</h2>
              <MetricTable data={gauges as [string, number][]} cols={[{ key: 'value', label: t('pages.settingsMetrics.colValue') }]} />
            </section>
          </>
        )}

        {timers.length > 0 && (
          <>
            {(counters.length + gauges.length > 0) && <Separator />}
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.settingsMetrics.sectionTimers')}</h2>
              <MetricTable data={timers as [string, DistStats][]} cols={distCols} />
            </section>
          </>
        )}

        {histograms.length > 0 && (
          <>
            {(counters.length + gauges.length + timers.length > 0) && <Separator />}
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.settingsMetrics.sectionHistograms')}</h2>
              <MetricTable data={histograms as [string, DistStats][]} cols={distCols} />
            </section>
          </>
        )}
      </div>
    </>
  );
}
